import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Tabs, Tab, Paper, Typography, List, ListItem, ListItemButton, ListItemText,
  Divider, CircularProgress, Checkbox, FormControlLabel, Button, Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import BreadcrumbsContainer from '../../containers/common/breadcurmbs/BreadcrumbsContainer';
import MenuContainer from '../../containers/domain/MenuContainer';
import AuthStatusPanel from '../../components/common/AuthStatusPanel';
import * as domainApi from '../../lib/api/domain';
import * as departmentApi from '../../lib/api/department';
import client from '../../lib/api/client';

/*
 * ksm_backend/ksm_frontend 포팅(2026-10-19, bito 3단계) — "메뉴 관리"/"권한 관리"(옛 사원별
 * 메뉴관리)를 메뉴/부서별 권한/사용자별 권한 탭 하나로 통합한 화면.
 *
 * ksm은 탭에 "권한별 사용자"/"사용자별 권한(역할)"(AuthStatusPanel, HR_USR_AUTH 역할코드 배정)과
 * "생산 설정"(ManufacturingSettingPanel)도 더 있었다. 전자는 ResponsiveDataGrid 의존 때문에
 * bito 3단계에는 보류했었는데, 4단계 UI 현대화에서 ResponsiveDataGrid가 들어왔으므로 이번에
 * AuthStatusPanel + 백엔드 /common/auth/usr/status(HrUsrAuthService#getAuthStatusList, bito에
 * 없던 조각만 포팅)까지 포팅해 탭을 되살렸다. 후자(ManufacturingSettingPanel)는 bito에 없는
 * 제조 모듈 설정이라 계속 제외한다.
 *
 * 관리자 체크: 이 화면이 쓰는 API(메뉴 생성/수정/삭제, 메뉴 권한 조회/저장)는 백엔드에서
 * 이미 관리자만 허용하도록 막아뒤서(MenuService.assertAdmin 등) 실제 보안은 서버가 담당하지만,
 * 일반 사용자가 주소를 직접 쳐서 들어왔을 때 빈 화면이 403 에러로 깨지는 것보다 안내 문구를
 * 보여주는 게 낫다 - ksm 에는 없는, bito 3단계 신규 요구사항.
 */

const isAdminRole = (user) => {
  const role = user?.role || '';
  return typeof role === 'string' && role.toUpperCase().includes('ADMIN');
};

// ── 메뉴 권한 관리 패널 (부서별/사원별 공통) ──────────────────
const MenuAuthPanel = ({ authType }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [targets, setTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [menuList, setMenuList] = useState([]);
  const [parentMenus, setParentMenus] = useState([]);
  const [checkedMenus, setCheckedMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // 왼쪽 목록 로드 (부서 or 사원)
  useEffect(() => {
    const loadTargets = async () => {
      setLoading(true);
      try {
        if (authType === 'DEPT') {
          const res = await departmentApi.getListDepartments();
          const list = res.data?.list || res.data || [];
          setTargets(list.filter((d) => d.useYn === 'Y'));
        } else {
          const res = await client.get('/api/v1/user/list');
          const list = res.data?.list || res.data || [];
          setTargets(list.filter((e) => e.useYn === 'Y'));
        }
      } catch {
        enqueueSnackbar('목록 조회 실패', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    loadTargets();
    setSelectedTarget(null);
    setCheckedMenus([]);
  }, [authType, enqueueSnackbar]);

  // 메뉴 전체 목록 로드
  useEffect(() => {
    const loadMenus = async () => {
      try {
        const res = await domainApi.getListMenus();
        if (res.data?.list) {
          setMenuList(res.data.list);
          setParentMenus(res.data.list.filter((m) => !m.parentUuid));
        }
      } catch {
        enqueueSnackbar('메뉴 목록 조회 실패', { variant: 'error' });
      }
    };
    loadMenus();
  }, [enqueueSnackbar]);

  // 대상 선택 시 권한 조회
  const handleSelectTarget = useCallback(async (targetId) => {
    setSelectedTarget(targetId);
    try {
      const res = await domainApi.getAuth({ authType, targetId });
      setCheckedMenus(res.data || []);
    } catch {
      setCheckedMenus([]);
    }
  }, [authType]);

  // 체크 변경
  const handleToggleMenu = (menuUuid) => {
    setCheckedMenus((prev) =>
      prev.includes(menuUuid)
        ? prev.filter((id) => id !== menuUuid)
        : [...prev, menuUuid]
    );
  };

  // 상위 메뉴 전체 체크/해제
  const handleToggleParent = (parentUuid, children) => {
    const allUuids = [parentUuid, ...children.map((c) => c.menuUuid)];
    const allChecked = allUuids.every((id) => checkedMenus.includes(id));
    if (allChecked) {
      setCheckedMenus((prev) => prev.filter((id) => !allUuids.includes(id)));
    } else {
      setCheckedMenus((prev) => [...new Set([...prev, ...allUuids])]);
    }
  };

  // 저장
  const handleSave = async () => {
    if (!selectedTarget) return;
    setSaving(true);
    try {
      await domainApi.saveAuth({
        authType,
        targetId: selectedTarget,
        menuUuids: checkedMenus,
      });
      enqueueSnackbar('메뉴 권한이 저장되었습니다.', { variant: 'success' });
    } catch {
      enqueueSnackbar('저장에 실패했습니다.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 260px)' }}>
      {/* 왼쪽: 부서/사원 목록 */}
      <Paper sx={{ width: 260, flexShrink: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ backgroundColor: '#424242', color: '#fff', py: 1, px: 2 }}>
          <Typography variant="subtitle2">
            {authType === 'DEPT' ? '부서 목록' : '사용자 목록'}
          </Typography>
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List sx={{ overflowY: 'auto', flex: 1 }}>
            {targets.map((item, index) => {
              const id = authType === 'DEPT'
                ? (item.dptUuid || item.id)
                : (item.usrUuid || item.id);
              const label = authType === 'DEPT'
                ? (item.dptNm || item.name || '-')
                : `${item.usrNm || ''} (${item.dptNm || ''})`;
              return (
                <ListItem key={index} dense disablePadding>
                  <ListItemButton
                    selected={selectedTarget === id}
                    onClick={() => handleSelectTarget(id)}
                  >
                    <ListItemText primary={<Typography variant="body2">{label}</Typography>} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>

      {/* 오른쪽: 메뉴 체크리스트 */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {!selectedTarget ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="text.secondary">
              왼쪽에서 {authType === 'DEPT' ? '부서' : '사용자'}를 선택하세요
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                체크된 메뉴에 접근 권한이 부여됩니다.
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                저장
              </Button>
            </Box>
            <Paper elevation={1} sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
              {parentMenus.map((parent) => {
                const children = menuList.filter((m) => m.parentUuid === parent.menuUuid);
                const allUuids = [parent.menuUuid, ...children.map((c) => c.menuUuid)];
                const allChecked = allUuids.every((id) => checkedMenus.includes(id));
                const someChecked = allUuids.some((id) => checkedMenus.includes(id));

                return (
                  <Box key={parent.menuUuid} sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allChecked}
                          indeterminate={someChecked && !allChecked}
                          onChange={() => handleToggleParent(parent.menuUuid, children)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body1" fontWeight={600}>
                          {parent.menuTitle}
                        </Typography>
                      }
                    />
                    {children.length > 0 && (
                      <Box sx={{ ml: 4 }}>
                        {children.map((child) => (
                          <FormControlLabel
                            key={child.menuUuid}
                            control={
                              <Checkbox
                                checked={checkedMenus.includes(child.menuUuid)}
                                onChange={() => handleToggleMenu(child.menuUuid)}
                                size="small"
                              />
                            }
                            label={<Typography variant="body2">{child.menuTitle}</Typography>}
                            sx={{ display: 'block' }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
};

// ── 메인 페이지 ──────────────────────────────────────────────
const TAB_LABELS = ['메뉴', '부서별 권한', '사용자별 권한', '권한별 사용자', '사용자별 권한(역할)'];

const MenuEditPage = () => {
  const [tab, setTab] = useState(0);
  const { user } = useSelector(({ auth }) => ({ user: auth.user }));

  if (!isAdminRole(user)) {
    return (
      <>
        <BreadcrumbsContainer title="공통관리/메뉴 관리" icon="common" />
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">관리자만 접근할 수 있는 화면입니다.</Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <BreadcrumbsContainer title={`공통관리/메뉴 관리/${TAB_LABELS[tab]}`} icon="common" />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          {TAB_LABELS.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ px: 1 }}>
        {tab === 0 && <MenuContainer />}
        {tab === 1 && <MenuAuthPanel authType="DEPT" />}
        {tab === 2 && <MenuAuthPanel authType="USER" />}
        {tab === 3 && <AuthStatusPanel viewType="byAuth" />}
        {tab === 4 && <AuthStatusPanel viewType="byUser" />}
      </Box>
    </>
  );
};

export default MenuEditPage;
